/*
 * Author: zoesvision.com
 * Purpose: Isolated visible-video playback and content reveal behavior for z-adaptive-grid.
 */

if (!customElements.get('z-adaptive-grid')) {
  customElements.define(
    'z-adaptive-grid',
    class ZAdaptiveGrid extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.abortController = new AbortController();
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        // zoesvision.com | 2026-08-15 | Purpose: Keep adaptive-grid mobile behavior active through tablet widths.
        this.mobileViewport = window.matchMedia('(max-width: 989px)');
        this.mobileCarousels = new Map();
        this.visibleCards = new Set();
        this.setupGroups();
        this.tabResizeObserver = new ResizeObserver(() => this.updateTabIndicator());
        if (this.tabsTrack) this.tabResizeObserver.observe(this.tabsTrack);
        this.cards = Array.from(this.querySelectorAll('[data-z-grid-card]'));
        this.handleEntries = this.handleEntries.bind(this);
        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleCarouselLayout = this.handleCarouselLayout.bind(this);
        this.motionQuery.addEventListener('change', this.handleMotionChange);
        this.mobileViewport.addEventListener('change', this.handleCarouselLayout);
        this.addEventListener('shopify:block:select', this.handleBlockSelect, {
          signal: this.abortController.signal
        });
        this.observer = new IntersectionObserver(this.handleEntries, { threshold: 0.15 });
        this.cards.forEach((card) => this.observer.observe(card));
        this.handleCarouselLayout();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.tabResizeObserver?.disconnect();
        this.motionQuery?.removeEventListener('change', this.handleMotionChange);
        this.mobileViewport?.removeEventListener('change', this.handleCarouselLayout);
        this.destroyMobileCarousels(false);
        this.abortController?.abort();
        this.pauseAllVideos();
        this.initialized = false;
      }

      setupGroups() {
        const content = this.querySelector('[data-z-grid-content]');
        if (!content) return;

        const allGroups = Array.from(content.children).filter((child) =>
          child.matches('[data-z-grid-group]')
        );
        this.groups = allGroups.slice(0, 3);
        const scrollRoot = this.closest('z-animate-on-scroll');
        this.header = scrollRoot?.querySelector('[data-z-grid-header]');
        this.tabsHost = scrollRoot?.querySelector('[data-z-grid-tabs]');
        this.tabsTrack = this.tabsHost?.querySelector('[data-z-grid-tabs-track]');
        // zoesvision.com | 2026-07-28 | Purpose: Detect the reusable static heading rather than legacy section markup.
        const hasHeading = Boolean(this.header?.querySelector('.z-heading:not([hidden])'));
        allGroups.slice(3).forEach((group) => {
          group.hidden = true;
        });

        if (!this.groups.length) {
          this.carouselTracks = [content];
          if (this.header) this.header.hidden = !hasHeading;
          this.dataset.zGroupsReady = 'true';
          scrollRoot?.refresh?.();
          return;
        }

        const legacyCards = Array.from(content.children).filter((child) =>
          child.matches('[data-z-grid-card]')
        );
        const firstGrid = this.groups[0].querySelector('[data-z-grid-group-grid]');
        if (legacyCards.length && firstGrid) firstGrid.prepend(...legacyCards);

        content.classList.add('z-adaptive-grid__grid--groups');
        this.carouselTracks = this.groups
          .map((group) => group.querySelector('[data-z-grid-group-grid]'))
          .filter(Boolean);
        const showTabs = this.groups.length > 1;
        if (this.header) {
          this.header.hidden = !hasHeading && !showTabs;
        }

        if (this.tabsHost && this.tabsTrack && showTabs) {
          this.tabsHost.hidden = false;
          this.tabsTrack.setAttribute('role', 'tablist');
          this.tabsTrack.replaceChildren();
          this.tabs = this.groups.map((group, index) => this.createTab(group, index));
        }

        this.activateGroup(0);
        this.dataset.zGroupsReady = 'true';
        scrollRoot?.refresh?.();
      }

      // zoesvision.com | 2026-08-10 | Purpose: Own the opt-in mobile Embla lifecycle without changing desktop Grid behavior.
      handleCarouselLayout() {
        if (this.dataset.zMobileLayout !== 'carousel' || !this.mobileViewport.matches) {
          this.destroyMobileCarousels();
          return;
        }

        this.initializeMobileCarousels();
      }

      initializeMobileCarousels() {
        if (typeof globalThis.EmblaCarousel !== 'function') {
          this.carouselDependencyAttempts = (this.carouselDependencyAttempts || 0) + 1;
          if (this.carouselDependencyAttempts <= 120 && this.isConnected) {
            this.carouselDependencyFrame = requestAnimationFrame(() => {
              this.initializeMobileCarousels();
            });
          }
          return;
        }

        this.carouselDependencyAttempts = 0;
        this.carouselTracks?.forEach((track) => {
          if (this.mobileCarousels.has(track) || !track.parentElement) return;

          const embla = globalThis.EmblaCarousel(track.parentElement, {
            loop: false,
            align: 'start',
            containScroll: 'trimSnaps',
            slidesToScroll: 1,
            watchDrag: track.children.length > 1
          });
          /* zoesvision.com | 2026-08-16 | Purpose: Synchronize nested z-media and card-native video with the selected mobile grid card. */
          const sync = () => this.syncMobileCarousel(track, embla);
          embla.on('select', sync);
          embla.on('reInit', sync);
          this.mobileCarousels.set(track, embla);
          sync();
        });
      }

      destroyMobileCarousels(resumeGridVideos = true) {
        cancelAnimationFrame(this.carouselDependencyFrame);
        this.carouselDependencyFrame = undefined;
        this.carouselDependencyAttempts = 0;
        this.mobileCarousels?.forEach((embla, track) => {
          this.releaseTrackMedia(track);
          embla.destroy();
        });
        this.mobileCarousels?.clear();
        if (resumeGridVideos) {
          this.cards?.forEach((card) => {
            if (this.visibleCards.has(card) && !card.closest('[hidden]')) this.playVisibleVideo(card);
          });
        }
      }

      syncMobileCarousel(track, embla) {
        const selectedIndex = embla.selectedScrollSnap();
        const nextIndex = Math.min(selectedIndex + 1, track.children.length - 1);
        const groupVisible = !track.closest('[data-z-grid-group]')?.hidden;
        Array.from(track.children).forEach((card, index) => {
          const active = groupVisible && index === selectedIndex;
          const preload = groupVisible && (active || index === nextIndex);
          // zoesvision.com | 2026-08-19 | Purpose: Warm the active and next mobile grid videos without changing playback ownership.
          const activeVideo = this.getVisibleVideo(card);
          card.querySelectorAll('.z-grid-card__video').forEach((video) => {
            video.preload = preload && video === activeVideo ? 'metadata' : 'none';
          });
          card.querySelectorAll('z-media').forEach((media) => {
            if (media.closest('z-adaptive-grid') !== this) return;
            media.dispatchEvent(new CustomEvent('z:media-carousel-active', {
              detail: { active, preload }
            }));
          });
          if (active && this.visibleCards.has(card)) this.playVisibleVideo(card);
          else this.pauseCardVideos(card, preload);
        });
      }

      releaseTrackMedia(track) {
        track.querySelectorAll('z-media').forEach((media) => {
          if (media.closest('z-adaptive-grid') !== this) return;
          media.dispatchEvent(new CustomEvent('z:media-carousel-release'));
        });
      }

      createTab(group, index) {
        const button = document.createElement('button');
        const panelId = `${this.id}-panel-${index}`;
        const tabId = `${this.id}-tab-${index}`;

        button.type = 'button';
        button.className = 'z-adaptive-grid__tab';
        button.id = tabId;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panelId);
        button.textContent = group.dataset.zGridGroupLabel || `Group ${index + 1}`;
        button.addEventListener('click', () => this.activateGroup(index), {
          signal: this.abortController.signal
        });
        button.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), {
          signal: this.abortController.signal
        });

        group.id = panelId;
        group.setAttribute('role', 'tabpanel');
        group.setAttribute('aria-labelledby', tabId);
        this.tabsTrack.append(button);
        return button;
      }

      activateGroup(index, focus = false) {
        this.activeGroupIndex = Math.max(0, Math.min(index, this.groups.length - 1));
        this.pauseAllVideos();
        this.groups.forEach((group, groupIndex) => {
          group.hidden = groupIndex !== this.activeGroupIndex;
        });
        this.tabs?.forEach((tab, tabIndex) => {
          const active = tabIndex === this.activeGroupIndex;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        this.updateTabIndicator();
        if (focus) this.tabs?.[this.activeGroupIndex]?.focus();

        const activeGroup = this.groups[this.activeGroupIndex];
        if (this.tabsTrack?.dataset.zIndicatorReady === 'true' && !this.motionQuery.matches) {
          activeGroup.removeAttribute('data-z-tab-enter');
          void activeGroup.offsetWidth;
          activeGroup.setAttribute('data-z-tab-enter', '');
        }

        const activeTrack = this.groups[this.activeGroupIndex]?.querySelector(
          '[data-z-grid-group-grid]'
        );
        if (activeTrack) {
          requestAnimationFrame(() => {
            const embla = this.mobileCarousels?.get(activeTrack);
            embla?.reInit();
            this.mobileCarousels?.forEach((carousel, track) => {
              this.syncMobileCarousel(track, carousel);
            });
          });
        }
      }

      updateTabIndicator() {
        const activeTab = this.tabs?.[this.activeGroupIndex];
        if (!this.tabsTrack || !activeTab) return;

        this.tabsTrack.style.setProperty('--z-adaptive-grid-tab-indicator-x', `${activeTab.offsetLeft}px`);
        this.tabsTrack.style.setProperty('--z-adaptive-grid-tab-indicator-width', `${activeTab.offsetWidth}px`);
        if (this.tabsTrack.dataset.zIndicatorReady !== 'true') {
          requestAnimationFrame(() => {
            if (this.tabsTrack?.isConnected) this.tabsTrack.dataset.zIndicatorReady = 'true';
          });
        }
      }

      handleTabKeydown(event, index) {
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.groups.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.groups.length) % this.groups.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.groups.length - 1;
        if (nextIndex === index) return;

        event.preventDefault();
        this.activateGroup(nextIndex, true);
      }

      handleBlockSelect(event) {
        const group = event.target.closest?.('[data-z-grid-group]');
        const index = this.groups?.indexOf(group);
        if (index >= 0) this.activateGroup(index);

        const card = event.target.closest?.('[data-z-grid-card]');
        const track = card?.parentElement;
        if (!card || !this.carouselTracks?.includes(track)) return;

        const cardIndex = Array.from(track.children).indexOf(card);
        if (cardIndex < 0) return;
        requestAnimationFrame(() => this.mobileCarousels?.get(track)?.scrollTo(cardIndex, true));
      }

      handleEntries(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visibleCards.add(entry.target);
          else this.visibleCards.delete(entry.target);

          if (this.dataset.zMobileLayout === 'carousel' && this.mobileViewport.matches) {
            const track = entry.target.parentElement;
            const embla = this.mobileCarousels?.get(track);
            if (embla) this.syncMobileCarousel(track, embla);
          } else if (entry.isIntersecting && !entry.target.closest('[hidden]')) {
            this.playVisibleVideo(entry.target);
          } else {
            this.pauseCardVideos(entry.target);
          }
        });
      }

      handleMotionChange() {
        this.pauseAllVideos();
      }

      getVisibleVideo(card) {
        return Array.from(card.querySelectorAll('.z-grid-card__video')).find(
          (video) => getComputedStyle(video).display !== 'none'
        );
      }

      playVisibleVideo(card) {
        // zoesvision.com | 2026-08-19 | Purpose: Promote only the breakpoint-correct visible card video before native playback.
        const activeVideo = this.getVisibleVideo(card);
        card.querySelectorAll('.z-grid-card__video').forEach((video) => {
          video.preload = video === activeVideo ? 'metadata' : 'none';
        });
        this.pauseCardVideos(card, true);

        if (this.motionQuery.matches) return;

        activeVideo?.play().catch(() => {});
      }

      pauseCardVideos(card, keepWarm = this.visibleCards.has(card)) {
        card.querySelectorAll('.z-grid-card__video').forEach((video) => {
          video.pause();
          if (!keepWarm) video.preload = 'none';
        });
      }

      pauseAllVideos() {
        this.querySelectorAll('.z-grid-card__video').forEach((video) => video.pause());
      }
    }
  );
}
